import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import GlobalFooter from './components/common/GlobalFooter';
import CookieConsent from './components/common/CookieConsent';

// Survey & Analytics
import SurveyDashboard from "./components/SurveyDashboard";
import SurveyResponse from "./components/SurveyResponse";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import BatchReportCustomization from "../src/components/AnalyticsComponents/BatchReportCustomization";
import { authAPI } from './services/apiClient';
import { BusinessProvider } from './services/BusinessContext';

// Survey Creation / Management
import SurveyGenerator from "./components/SurveyManagement/SurveyGenerator";
import AIChat from "./components/SurveyManagement/AIChat";
import SurveyCreationEntry from './components/SurveyManagement/SurveyCreationEntry';
import GuidedSurveyBuilder from './components/SurveyManagement/GuidedSurveyBuilder';
import CreateSurvey from "./components/SurveyManagement/CreateSurvey";
import CreateSurveyForBusiness from "./components/SurveyManagement/CreateSurveyForBusiness";
import SavedSurveyList from "./components/SurveyManagement/SavedSurveyList";

// Additional Admin Tools
import "./styles/fonts.css"; // Import the fonts

// Import Analytics Components
import DemographicsFilterPanel from "./components/AnalyticsComponents/DemographicsFilterPanel";
import DemographicsSummary from "./components/AnalyticsComponents/DemographicsSummary";
import MergeAnalyticsDashboard from "./components/AnalyticsComponents/MergeAnalyticsDashboard";
import ResponseTimeAnalyticsPanel from "./components/AnalyticsComponents/ResponseTimeAnalyticsPanel";
import DropoutAnalysisPanel from "./components/AnalyticsComponents/DropoutAnalysisPanel";
import ReportDownloadPanel from "./components/AnalyticsComponents/ReportDownloadPanel";

// Authentication
import Login from "./components/Authentication/Login";
import LoginPasskey from "./components/Authentication/LoginPasskey";
import AccountActivation from "./components/Authentication/AccountActivation";
import ForgotPasswordRequest from "./components/Authentication/ForgotPasswordRequest";
import ResetPassword from "./components/Authentication/ResetPassword";

// Import SuperAdminDashboard
import SuperAdminDashboard from './components/SuperAdminDashboard';
import CreateBusiness from './components/admin/CreateBusiness';
import CreateBusinessAdmin from './components/admin/CreateBusinessAdmin';
import BusinessSpecificDashboard from './components/admin/BusinessSpecificDashboard';
import ManageBusinesses from './components/admin/ManageBusinesses';
import ManageBusinessSurveys from './components/admin/ManageBusinessSurveys';
import BusinessAdminDashboard from './components/admin/BusinessAdminDashboard';
import AdminSettings from './components/admin/AdminSettings';
import PurchaseAIPoints from './components/admin/PurchaseAIPoints';
import BusinessSubscriptionManagement from './components/admin/BusinessSubscriptionManagement';
import UserManagement from './components/admin/UserManagement';
import PurchaseResponses from './components/admin/PurchaseResponses';
import CreateBusinessAdminForBusiness from './components/admin/CreateBusinessAdminForBusiness';
import SurveyLinks from './components/admin/SurveyLinks';
import CreateAIPointsPackage from './components/admin/CreateAIPointsPackage';
import BusinessAIAccessUsage from './components/admin/BusinessAIAccessUsage';
import BusinessAIAnalyticsOverview from './components/admin/BusinessAIAnalyticsOverview';
import DeletedSurveysPage from './components/admin/DeletedSurveysPage';
import SurveyDeletionAuditLogPage from './components/admin/SurveyDeletionAuditLogPage';

// Test Data Generator
import GenerateResponses from './components/surveys/GenerateResponses';

import AdminLayout from './components/admin/AdminLayout';
import Homepage from './components/homepage/App';
import UpdatePassword from './components/common/UpdatePassword';
import LegalPages from './components/LegalPages';
import { clearLocalStorageExcept } from './utils/storage';

/**
 * ProtectedRoute:
 *   - Ensures user is logged in with a valid token
 *   - Allows roles: business_admin, super_admin (by default) or can be restricted
 */
const ProtectedRoute = ({ children, allowedRoles = ["business_admin", "super_admin"] }) => {
  const token = localStorage.getItem("token");
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      setIsLoading(true);
      
      const token = localStorage.getItem('token');
      const storedRole = localStorage.getItem('userRole');
      const storedUser = localStorage.getItem('user');
      
      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }
      
      try {
        const response = await authAPI.getCurrentUser();
        
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('userRole', response.data.role);
        
        setUserData(response.data.user);
        setUserRole(response.data.role);
        setIsAuthenticated(true);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          clearLocalStorageExcept();
        } else {
          if (storedUser && storedRole) {
            try {
              setUserData(JSON.parse(storedUser));
              setUserRole(storedRole);
              setIsAuthenticated(true);
            } catch (parseError) {
              clearLocalStorageExcept();
              setIsAuthenticated(false);
            }
          } else {
            setIsAuthenticated(false);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    validateToken();
  }, []);
  
  if (isLoading) {
    return (
      <div className="app-route-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  
  if (!allowedRoles.includes(userRole)) {
    return <Navigate to="/unauthorized" replace />;
  }
  
  return children;
};

/** AdminRoute: subset of ProtectedRoute restricted to Super Admin role only. */
const AdminRoute = ({ children }) => {
  return <ProtectedRoute allowedRoles={["super_admin"]}>{children}</ProtectedRoute>;
};

/**
 * For public routes that should redirect authenticated users
 */
const PublicOnlyRoute = ({ children }) => {
  const token = localStorage.getItem('token');

  if (token) {
    const userRole = localStorage.getItem('userRole');
    if (userRole === 'super_admin') {
      return <Navigate to="/admin" replace />;
    } else if (userRole === 'business_admin') {
      return <Navigate to="/business-admin/dashboard" replace />;
    }
  }

  return children;
};

/**
 * RootRedirect: Handles the root path "/" with proper authentication and role checks
 */
const RootRedirect = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('token');

      if (!token) {
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await authAPI.getCurrentUser();
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.setItem('userRole', response.data.role);
        setUserRole(response.data.role);
        setIsAuthenticated(true);
      } catch (error) {
        clearLocalStorageExcept();
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthAndRedirect();
  }, []);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  if (userRole === 'super_admin') {
    return <Navigate to="/admin" replace />;
  } else if (userRole === 'business_admin') {
    return <Navigate to="/business-admin/dashboard" replace />;
  } else {
    // If authenticated but no role matches, go to home as safe default or login
    // The requirement says "takes me to home by default", so let's default to home
    // even for logged in users who don't match specific admin roles if that makes sense,
    // or keep it as login/home. For safety and matching the request "takes me to home",
    // I will set the fallback to home as well.
    return <Navigate to="/home" replace />;
  }
};

import "./styles/Auth.css";

// Public pages designed on a dark background. Every other (logged-in) page
// uses the light page background from app-shell.css.
const DARK_BODY_PATHS = [
  '/login', '/login-passkey', '/forgot-password', '/reset-password', '/activate-account',
  '/home', '/services', '/legal', '/unauthorized',
];
const DARK_BODY_PREFIXES = ['/survey/', '/share/survey/'];

const isDarkBodyPath = (pathname) =>
  DARK_BODY_PATHS.includes(pathname) ||
  DARK_BODY_PREFIXES.some(prefix => pathname.startsWith(prefix));

const PAGE_TITLES = [
  ['/admin/business/new', 'Create Business'],
  ['/admin/business/manage', 'Manage Businesses'],
  ['/admin/surveys/deleted', 'Deleted Surveys'],
  ['/admin/audit-logs', 'Audit Log'],
  ['/admin/ai-analytics', 'AI Analytics'],
  ['/admin/users', 'User Management'],
  ['/admin/settings', 'Admin Settings'],
  ['/admin/super-admins/new', 'New Super Admin'],
  ['/admin', 'Super Admin Panel'],
  ['/business-admin/dashboard', 'Business Dashboard'],
  ['/create-survey', 'Create Survey'],
  ['/savedsurveys', 'Saved Surveys'],
  ['/survey-builder', 'Survey Builder'],
  ['/analytics/', 'Survey Analytics'],
  ['/login', 'Log In'],
  ['/legal', 'Terms and Policies'],
  ['/survey/', 'Survey'],
  ['/share/survey/', 'Survey'],
  ['/home', 'Home'],
];

const BodyStyleHandler = () => {
  const location = useLocation();

  useEffect(() => {
    const match = PAGE_TITLES.find(([prefix]) => location.pathname.startsWith(prefix));
    document.title = match ? `${match[1]} | Eclipseer` : 'Eclipseer Survey';

    if (isDarkBodyPath(location.pathname)) {
      document.body.classList.add('auth-active-body');
    } else {
      document.body.classList.remove('auth-active-body');
    }

    // Capture the body element to cleanup on unmount
    return () => {
      document.body.classList.remove('auth-active-body');
    };
  }, [location]);

  return null;
};

function App() {
  const [showCookieManager, setShowCookieManager] = React.useState(false);
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/business-admin');
  const isSurveyResponseRoute = location.pathname.startsWith('/survey/') || location.pathname.startsWith('/share/survey/');

  return (
    <>
      <BodyStyleHandler />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 7000,
          success: { duration: 7000 },
          error: { duration: 7000 },
          loading: { duration: 10000 },
        }}
      />
      <CookieConsent
        openManager={showCookieManager}
        onCloseManager={() => setShowCookieManager(false)}
        theme={isAdminRoute ? 'light' : 'dark'}
      />
      <Routes>
        {/* ========== AUTH ========== */}
        <Route path="/login" element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        } />
        <Route path="/login-passkey" element={
          <PublicOnlyRoute>
            <LoginPasskey />
          </PublicOnlyRoute>
        } />
        <Route path="/forgot-password" element={<ForgotPasswordRequest />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<AccountActivation />} />

        {/* ========== DEFAULT REDIRECT ========== */}
        <Route path="/" element={<RootRedirect />} />
        <Route path="/home" element={<Homepage initialPage="home" />} />
        <Route path="/services" element={<Homepage initialPage="services" />} />
        <Route path="/legal" element={<LegalPages />} />

        {/* ========== CONTEXT-AWARE SURVEY CREATION REDIRECTS ========== */}
        <Route
          path="/survey-builder"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "admin", "business_admin"]}>
              <BusinessProvider>
                <SurveyCreationEntry />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/dashboard"
          element={
            localStorage.getItem("token") ? (
              (() => {
                const userRole = localStorage.getItem("userRole");
                let user = {};
                try {
                  user = JSON.parse(localStorage.getItem("user") || "{}");
                } catch (parseError) {
                  user = {};
                }

                if (userRole === "business_admin" && user.business_id) {
                  return <Navigate to={`/admin/business/${user.business_id}/surveys/new`} replace />;
                } else if (userRole === "super_admin") {
                  return <Navigate to="/survey-builder" replace />;
                } else {
                  return <Navigate to="/login" replace />;
                }
              })()
            ) : (
              <Navigate to="/login" />
            )
          }
        />

        {/* ========== BUSINESS ADMIN DASHBOARD ========== */}
        <Route
          path="/business-admin/dashboard"
          element={
            <ProtectedRoute allowedRoles={["business_admin"]}>
              <BusinessProvider>
                <BusinessAdminDashboard />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />

        {/* ========== AI POINTS PURCHASE PAGE ========== */}
        <Route
          path="/business/purchase-points"
          element={
            <ProtectedRoute allowedRoles={["business_admin"]}>
              <BusinessProvider>
                  <PurchaseAIPoints />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />

        {/* ========== SUBSCRIPTION MANAGEMENT PAGE ========== */}
        <Route
          path="/business/subscription-management"
          element={
            <ProtectedRoute allowedRoles={["super_admin"]}>
              <BusinessProvider>
                  <BusinessSubscriptionManagement />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />

        {/* ========== UPDATE PASSWORD PAGE ========== */}
        <Route
          path="/user/update-password"
          element={
            <ProtectedRoute allowedRoles={["business_admin", "super_admin", "user"]}>
              <BusinessProvider>
                <UpdatePassword />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />
        {/* ========== SUPER ADMIN DASHBOARD ========== */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <SuperAdminDashboard />
            </AdminRoute>
          }
        />

        {/* Admin Layout Routes */}
        <Route element={
          // Wrap admin layout so Sidebar receives business context for permission checks
          <BusinessProvider>
            <AdminLayout />
          </BusinessProvider>
        }>
            {/* Route for creating a new business (Super Admin only) */}
            <Route
            path="/admin/business/new"
            element={
                <AdminRoute>
                <CreateBusiness />
                </AdminRoute>
            }
            />

            <Route
            path="/admin/business-admin/new"
            element={
                <AdminRoute>
                <CreateBusinessAdmin />
                </AdminRoute>
            }
            />

            <Route
            path="/admin/business/dashboard/:businessId"
            element={
                <AdminRoute>
                <BusinessSpecificDashboard />
                </AdminRoute>
            }
            />

            {/* Route for managing businesses */}
            <Route
            path="/admin/business/manage"
            element={
                <AdminRoute>
                <ManageBusinesses />
                </AdminRoute>
            }
            />

            <Route
            path="/admin/business/manage/:businessId/ai-usage"
            element={
                <AdminRoute>
                <BusinessAIAccessUsage />
                </AdminRoute>
            }
            />

            <Route
            path="/admin/ai-analytics"
            element={
                <AdminRoute>
                <BusinessAIAnalyticsOverview />
                </AdminRoute>
            }
            />

            <Route
            path="/admin/settings"
            element={
                <AdminRoute>
                    <AdminSettings />
                </AdminRoute>
            }
            />
            <Route
            path="/admin/surveys/deleted"
            element={
                <AdminRoute>
                    <DeletedSurveysPage />
                </AdminRoute>
            }
            />
            <Route
            path="/admin/audit-logs"
            element={
                <AdminRoute>
                    <SurveyDeletionAuditLogPage />
                </AdminRoute>
            }
            />
            <Route
            path="/admin/surveys/deletion-audit-logs"
            element={
                <AdminRoute>
                    <Navigate to="/admin/audit-logs" replace />
                </AdminRoute>
            }
            />
            <Route
            path="/admin/super-admins/new"
            element={
                <AdminRoute>
                    <AdminSettings />
                </AdminRoute>
            }
            />

            {/* Route for managing business surveys */}
            <Route
            path="/admin/business/:businessId/surveys/manage"
            element={
                <ProtectedRoute allowedRoles={["super_admin", "business_admin"]}>
                <BusinessProvider>
                    <ManageBusinessSurveys />
                </BusinessProvider>
                </ProtectedRoute>
            }
            />

            {/* Route for managing survey links */}
            <Route
            path="/admin/business/:businessId/surveys/:surveyId/links"
            element={
                <ProtectedRoute allowedRoles={["super_admin", "business_admin"]}>
                <BusinessProvider>
                    <SurveyLinks />
                </BusinessProvider>
                </ProtectedRoute>
            }
            />

            <Route
            path="/admin/business/:businessId/admins/new"
            element={
                <ProtectedRoute allowedRoles={["super_admin", "admin", "business_admin"]}>
                <BusinessProvider>
                    <CreateBusinessAdminForBusiness />
                </BusinessProvider>
                </ProtectedRoute>
            }
            />

            <Route path="/admin/users" element={<AdminRoute><UserManagement /></AdminRoute>} />
            <Route path="/admin/users/manage" element={<AdminRoute><UserManagement /></AdminRoute>} />

            {/* Route for creating surveys for a specific business */}
            <Route
            path="/admin/business/:businessId/surveys/new"
            element={
                <ProtectedRoute allowedRoles={["super_admin", "business_admin"]}>
                <BusinessProvider>
                    <CreateSurveyForBusiness />
                </BusinessProvider>
                </ProtectedRoute>
            }
            />

            {/* Route for editing surveys for a specific business */}
            <Route
            path="/admin/business/:businessId/surveys/:surveyId/edit"
            element={
                <ProtectedRoute allowedRoles={["super_admin", "business_admin"]}>
                <BusinessProvider>
                    <CreateSurveyForBusiness />
                </BusinessProvider>
                </ProtectedRoute>
            }
            />

            {/* Route for super admin to create standalone surveys */}
            <Route
            path="/create-survey"
            element={
                <AdminRoute>
                    <CreateSurvey />
                </AdminRoute>
            }
            />
        </Route>

        {/* ========== SURVEY DASHBOARD (For Survey Listing/Stats) ========== */}
        <Route
          path="/survey-dashboard"
          element={
            <AdminRoute>
              <SurveyDashboard />
            </AdminRoute>
          }
        />

        {/* ========== SAVED SURVEYS LIST (ADMIN ONLY) ========== */}
        <Route
          path="/savedsurveys"
          element={
            <AdminRoute>
              <SavedSurveyList />
            </AdminRoute>
          }
        />

        {/* ========== SURVEY RESPONSE (PUBLIC) ========== */}
        <Route path="/survey/:idOrCode/:linkCode?" element={<SurveyResponse />} />
        <Route path="/share/survey/:idOrCode/:linkCode?" element={<SurveyResponse />} />

        {/* ========== ANALYTICS DASHBOARD (ADMIN ONLY) ========== */}
        <Route
          path="/analytics/:surveyId/:businessId?"
          element={
            <ProtectedRoute allowedRoles={[ "admin", "business_admin", "super_admin"]}>
              <BusinessProvider>
                <AnalyticsDashboard />
              </BusinessProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<BatchReportCustomization />} />
          <Route path="demographics" element={<DemographicsFilterPanel />} />
          <Route path="demographics-summary" element={<DemographicsSummary />} />
          <Route path="merge-links" element={<MergeAnalyticsDashboard />} />
          <Route path="response-time" element={<ResponseTimeAnalyticsPanel />} />
          <Route path="dropout" element={<DropoutAnalysisPanel />} />
          <Route path="report" element={<ReportDownloadPanel />} />
          <Route path="batch-customization" element={<BatchReportCustomization />} />
        </Route>

        {/* ========== SURVEY CREATION & AI TOOLS ========== */}
        <Route
          path="/survey-editor"
          element={
            <ProtectedRoute allowedRoles={["super_admin", "admin", "business_admin"]}>
              <SurveyGenerator />
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-chat"
          element={
            <AdminRoute>
              <AIChat />
            </AdminRoute>
          }
        />
        {/* ========== SURVEY BUILDER SUB-ROUTES ========== */}
        <Route path="/survey-builder/quick" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "business_admin"]}>
            <BusinessProvider>
              <AIChat />
            </BusinessProvider>
          </ProtectedRoute>
        } />
        <Route path="/survey-builder/guided" element={
          <ProtectedRoute allowedRoles={["super_admin", "admin", "business_admin"]}>
            <BusinessProvider>
              <GuidedSurveyBuilder />
            </BusinessProvider>
          </ProtectedRoute>
        } />
        <Route path="/ai-survey-builder/*" element={<Navigate to="/survey-builder" replace />} />

        {/* ========== RESPONSE QUOTA PURCHASE PAGE ========== */}
        <Route
          path="/business/purchase-responses"
          element={
            <ProtectedRoute allowedRoles={["business_admin"]}>
              <BusinessProvider>
                <PurchaseResponses />
              </BusinessProvider>
            </ProtectedRoute>
          }
        />

        {/* ========== AI POINTS PACKAGES (ADMIN ONLY) ========== */}
        <Route
          path="/admin/ai-points-packages/new"
          element={
            <AdminRoute>
              <CreateAIPointsPackage />
            </AdminRoute>
          }
        />
        
        {/* Test Data Generation */}
        <Route
          path="/admin/surveys/:surveyId/generate-responses"
          element={
            <ProtectedRoute allowedRoles={["admin", "business_admin", "super_admin"]}>
              <GenerateResponses />
            </ProtectedRoute>
          }
        />

        {/* ========== UNAUTHORIZED & 404 ========== */}
        <Route
          path="/unauthorized"
          element={
            <div style={{ textAlign: "center", padding: "50px 20px" }}>
              <h2>Unauthorized Access</h2>
              <p>You don't have permission to access this page.</p>
              <button
                onClick={() => window.history.back()}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginTop: "20px",
                }}
              >
                Go Back
              </button>
            </div>
          }
        />

        <Route
          path="*"
          element={
            <div style={{ textAlign: "center", padding: "50px 20px" }}>
              <h2>404 - Page Not Found</h2>
              <p>The page you're looking for doesn't exist.</p>
              <button
                onClick={() => (window.location.href = "/")}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#007bff",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  marginTop: "20px",
                }}
              >
                Go Home
              </button>
            </div>
          }
        />
      </Routes>
      {!isSurveyResponseRoute && (
        <GlobalFooter onManageCookies={() => setShowCookieManager(true)} />
      )}
    </>
  );
}

export default App;
